import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDatabase,
  faHardDrive,
  faTriangleExclamation,
  faBoxArchive,
  faCircleNodes,
  faHeartPulse,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { getAssetStats, getOverallHealth, getAlerts } from './api/ame-platform.api';
import type { StorageStatsDto, HealthStatusDto, AlertDto } from './api/ame-platform.api';
import { formatByteString } from './components/FileSizeDisplay';
import { ProviderBreakdownPanel } from './components/ProviderBreakdownPanel';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';

interface KpiCardProps {
  label: string;
  value: string;
  icon: typeof faDatabase;
  iconColor: string;
  loading?: boolean;
  to?: string;
}

function KpiCard({ label, value, icon, iconColor, loading = false, to }: KpiCardProps) {
  // Stable, label-derived hook so a KPI can be asserted without colliding with
  // the same number rendered elsewhere on the page (the provider breakdown
  // legitimately repeats these values).
  const testId = `kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const content = (
    <div
      data-testid={testId}
      className="flex items-start gap-4 rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary/40"
    >
      <div
        className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', iconColor)}
      >
        <FontAwesomeIcon icon={icon} className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {loading ? (
          <div className="mt-1 h-7 w-20 animate-pulse rounded bg-muted" />
        ) : (
          <p className="mt-0.5 text-2xl font-semibold text-foreground">{value}</p>
        )}
      </div>
      {to && (
        <FontAwesomeIcon
          icon={faArrowRight}
          className="h-3.5 w-3.5 mt-1 text-muted-foreground shrink-0"
        />
      )}
    </div>
  );

  if (to) return <Link to={to}>{content}</Link>;
  return content;
}

const ALERT_COLORS = {
  critical: 'border-l-destructive bg-destructive/5 text-destructive',
  warning: 'border-l-amber-500 bg-amber-500/5 text-amber-700 dark:text-amber-400',
  info: 'border-l-primary bg-primary/5 text-primary',
};

export function AssetDashboardPage() {
  const [stats, setStats] = useState<StorageStatsDto | null>(null);
  const [health, setHealth] = useState<HealthStatusDto | null>(null);
  const [alerts, setAlerts] = useState<AlertDto[]>([]);
  const [loading, setLoading] = useState(true);
  /**
   * A failed load must be VISIBLE.
   *
   * This page previously swallowed every error with `.catch(() => undefined)`,
   * so when `/assets/stats` answered HTTP 500 the operator saw a confident
   * "0 objects / 0 B / UNKNOWN" instead of "this did not load". A dashboard that
   * reports zero when it means "I failed" is worse than one that reports
   * nothing, because zero is a believable number.
   */
  const [error, setError] = useState<string | null>(null);
  const platformRole = usePlatformAuthStore((st) => st.user?.role);
  const canManage = useMemo(
    () => canPlatform(platformRole, 'platform:system:manage'),
    [platformRole],
  );

  useEffect(() => {
    setLoading(true);
    // `allSettled`, not `all`: one failing panel must not blank the others.
    void Promise.allSettled([getAssetStats(), getOverallHealth(), getAlerts()])
      .then(([s, h, a]) => {
        const failures: string[] = [];
        if (s.status === 'fulfilled') setStats(s.value);
        else failures.push('storage statistics');
        if (h.status === 'fulfilled') setHealth(h.value);
        else failures.push('storage health');
        if (a.status === 'fulfilled') setAlerts(a.value);
        else failures.push('alerts');
        setError(
          failures.length > 0
            ? `Could not load ${failures.join(', ')}. The figures below are incomplete.`
            : null,
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const activeCount = stats?.objectsByStatus['ACTIVE'] ?? 0;
  const softDeleted = stats?.objectsByStatus['SOFT_DELETED'] ?? 0;
  const quarantined = stats?.objectsByStatus['QUARANTINED'] ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Asset Management</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Platform-wide storage overview across all tenants
        </p>
      </div>

      {error !== null && (
        <div
          className="rounded-xl border border-destructive/30 bg-destructive/5 p-4"
          data-testid="dashboard-error"
        >
          <p className="flex items-start gap-2 text-sm text-destructive">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        </div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
        <KpiCard
          label="Total Objects"
          value={loading ? '…' : (stats?.totalObjects ?? 0).toLocaleString()}
          icon={faDatabase}
          iconColor="bg-primary/[0.12] text-primary"
          loading={loading}
          to={PLATFORM_ROUTES.STORAGE_ASSETS}
        />
        <KpiCard
          label="Catalogued Size"
          value={loading ? '…' : formatByteString(stats?.totalBytes)}
          icon={faHardDrive}
          iconColor="bg-primary/[0.12] text-primary"
          loading={loading}
        />
        <KpiCard
          label="Active Assets"
          value={loading ? '…' : activeCount.toLocaleString()}
          icon={faDatabase}
          iconColor="bg-green-500/10 text-green-600 dark:text-green-400"
          loading={loading}
        />
        <KpiCard
          label="Quarantined"
          value={loading ? '…' : (softDeleted + quarantined).toLocaleString()}
          icon={faBoxArchive}
          iconColor="bg-amber-500/10 text-amber-600"
          loading={loading}
          to={PLATFORM_ROUTES.STORAGE_QUARANTINE}
        />
        <KpiCard
          label="Storage Health"
          value={loading ? '…' : (health?.status ?? 'unavailable').toUpperCase()}
          icon={faHeartPulse}
          iconColor={
            health?.status === 'healthy'
              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
              : health?.status === 'degraded'
                ? 'bg-amber-500/10 text-amber-600'
                : 'bg-destructive/10 text-destructive'
          }
          loading={loading}
          to={PLATFORM_ROUTES.STORAGE_HEALTH}
        />
      </div>

      <ProviderBreakdownPanel canManage={canManage} />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Objects by Module */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4">
            <h2 className="text-sm font-semibold text-foreground">Assets by Module</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Active assets grouped by source module
            </p>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  <div className="h-4 w-10 animate-pulse rounded bg-muted" />
                </div>
              ))}
            </div>
          ) : stats && Object.keys(stats.objectsByModule).length > 0 ? (
            <div>
              {Object.entries(stats.objectsByModule)
                .sort(([, a], [, b]) => b - a)
                .map(([module, count]) => (
                  <div
                    key={module}
                    className="flex items-center justify-between border-b border-border py-2.5 last:border-0"
                  >
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faCircleNodes}
                        className="h-3.5 w-3.5 text-muted-foreground"
                      />
                      <span className="text-sm text-foreground capitalize">{module}</span>
                    </div>
                    <span className="text-sm font-medium text-foreground">
                      {count.toLocaleString()}
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">No assets yet</p>
          )}
        </div>

        {/* Alerts */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Active Alerts</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Storage provider and health alerts
              </p>
            </div>
            <Link
              to={PLATFORM_ROUTES.STORAGE_HEALTH}
              className="text-xs text-primary hover:underline underline-offset-2"
            >
              View Health →
            </Link>
          </div>
          {loading ? (
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
              ))}
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <FontAwesomeIcon
                icon={faHeartPulse}
                className="h-6 w-6 text-green-500 dark:text-green-400 mb-2"
              />
              <p className="text-sm font-medium text-foreground">All systems healthy</p>
              <p className="mt-1 text-xs text-muted-foreground">No active storage alerts</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={cn('rounded-lg border-l-4 px-3 py-2.5', ALERT_COLORS[alert.severity])}
                >
                  <div className="flex items-start gap-2">
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase">{alert.severity}</p>
                      <p className="text-xs">{alert.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
